// Day Drawer's own Photos tab content — deliberately NOT Table's
// TableMealPhotosPanel.js (wraps everything in a `.log-card.log-card--meal-photos`
// with its own colored header/background) nor Log's LogMealPhotosCard.js
// (flat 3-photo cap, same wrapper). This drawer already has its own header
// and tabs ("Photos" is the tab label itself), so wrapping the content in
// ANOTHER titled, colored card would be redundant — this renders just the
// meal-type photo groups + thumbnails, no card chrome.
// SYNC NOTE: duplicates TableMealPhotosPanel's photo-grouping/markup on
// purpose (isolation over reuse). Carousel open/close state itself no longer
// is duplicated — it lives in the shared useMealPhotosCarousel hook, also
// used by LogMealPhotosCard.js and TableMealPhotosPanel.js.
import PropTypes from "prop-types";
import moment from "moment";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import PhotosCarousel from "../../../metrics/meal-photos/components/PhotosCarousel";
import { useMealPhotosCarousel } from "../../../metrics/meal-photos/useMealPhotosCarousel";
import { mealOrderIndex } from "./viewTokens";

// Formats the UTC date into a readable format
const formatUtcDate = (utcDate) => {
  if (!utcDate) return "";
  const dateStr = utcDate.toString();
  if (dateStr.length !== 8) return "";
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const dayNum = dateStr.substring(6, 8);
  const date = moment(`${year}-${month}-${dayNum}`);
  return date.isValid() ? date.format("MMM D, YYYY") : "";
};

// Groups the photos by meal type
const groupByMealType = (photos) => {
  const byType = new Map();
  photos.forEach((photo) => {
    if (!byType.has(photo.mealType)) byType.set(photo.mealType, []);
    byType.get(photo.mealType).push(photo);
  });
  const orderedTypes = [...byType.keys()].sort((a, b) => mealOrderIndex(a) - mealOrderIndex(b));
  return orderedTypes.map((mealType) => [mealType, byType.get(mealType)]);
};

// Renders the Day Drawer Photos Card
const DayDrawerPhotosCard = ({ stat, utcDate, locals }) => {
  const { t } = useTranslation("athlete-portal");

  const allPhotos = useMemo(() => {
    return Object.entries(stat?.mealPhotos || {}).flatMap(([mealType, mealData]) =>
      (mealData.list || []).map((photo) => ({ ...photo, mealType, mealTags: mealData.tags || [] }))
    );
  }, [stat]);

  const { showCarousel, selectedPhotoIndex, setSelectedPhotoIndex, openCarousel, closeCarousel } =
    useMealPhotosCarousel({ allPhotos, dailyStatId: stat?._id });

  if (allPhotos.length === 0) return null;

  return (
    <>
      <div className="np-table-detail-photo-groups">
        {groupByMealType(allPhotos).map(([mealType, photos]) => (
          <div className="np-table-detail-photo-group" key={mealType}>
            <span className="np-table-detail-photo-group-label">{mealType}</span>
            <div className="log-card__photos">
              {photos.map((photo) => {
                const src = photo.image?.s3Path || photo.s3Path || photo.url;
                return (
                  <button
                    key={photo._id}
                    type="button"
                    className="log-card__photo-link"
                    onClick={() => openCarousel(photo._id)}
                    aria-label={t("calendar.cards.mealPhotos.viewAria")}
                  >
                    <img src={src} alt={t("calendar.cards.mealPhotos.alt")} className="log-card__photo-thumb" />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Portalled straight to <body>, bypassing .side-drawer's own DOM
          subtree entirely — .side-drawer (drawer.css) animates via
          `transform`, and ANY transform on an ancestor creates a new
          containing block for position:fixed descendants. PhotosCarousel's
          Modal (.Modal-Box, modal.css) is `fixed inset-0` expecting to
          cover the whole viewport; nested inside the drawer as-is, it gets
          boxed into the drawer's own ~420px width instead. Log/Table don't
          hit this — they render PhotosCarousel outside any transformed
          ancestor — so this is scoped to this file only, not
          PhotosCarousel.js/Modal/drawer.css themselves. */}
      {typeof document !== "undefined" &&
        createPortal(
          <PhotosCarousel
            photos={allPhotos}
            selectedPhotoIndex={selectedPhotoIndex}
            isOpen={showCarousel}
            onClose={closeCarousel}
            onPhotoChange={setSelectedPhotoIndex}
            date={formatUtcDate(utcDate)}
            locals={locals}
            utcDate={utcDate}
          />,
          document.body
        )}
    </>
  );
};

DayDrawerPhotosCard.propTypes = {
  stat: PropTypes.object.isRequired,
  utcDate: PropTypes.number,
  locals: PropTypes.object,
};

export default DayDrawerPhotosCard;
